import { LEARN_BASICS } from './learnBasics.generated';

/**
 * E56 · Learn 2 — the DATA of the Learn library that both the phone and the portal read (the phone's catalogues,
 * E34's homework labels, the portal's pickers). Pure data, no logic: the rules over it live in `@cc/domain`
 * (`wellness/learning`, passed these tables as parameters — a catalogue is a parameter there, as in E15).
 *
 * THREE TABLES, and the founder's answers (docs/content/learn/README.md §6) are why:
 *   1. `LEARN_RETIRED_ARTICLES` — the ids that left the app (Q-learn-2 B, Q-learn-3 A, Q-learn-20 A) and what a reference to
 *      each still means (BUILD.md §2). Their titles live on ONLY here.
 *   2. `LEARN_FILMS` — the videos catalogue: Dmitri's seven Russian lectures, then the ten new Learn films
 *      (Q-learn-4…6). A film with no YouTube id yet does not render (BUILD.md §3).
 *   3. `LEARN_RETIRED_VIDEOS` — the seven old English clips (Q-learn-7 A): they keep their ids and their names so
 *      a worksheet or homework that already references one still plays it and still shows its title.
 *
 * ─── FILLING IN A FILM (the founder uploads later) ────────────────────────────────────────────────────────────────
 * One line in `LEARN_FILM_UPLOADS` below: replace `null` with `{ id: '<YouTube id>', duration: <seconds> }`.
 * The key is `<film slug>:<language>`. The phone gets it by OTA; the API's worksheet validator (`LEARN_VIDEO_IDS`)
 * and the portal's pickers read this same table, so they redeploy with the change.
 */

export type LearnRetiredArticleStatus =
  | 'replacedBy'
  | 'movedToWebsite'
  | 'retired';

export type LearnRetiredArticleEntry = {
  status: LearnRetiredArticleStatus;
  /** The title the reference keeps, in both languages. */
  title: { en: string; ru: string };
  /** `replacedBy` — the Basics article that opens instead. */
  replacedBy?: string;
  /** `movedToWebsite` — the website slug of the article (RU adds `/ru` in front). */
  movedTo?: string;
};

/**
 * BUILD.md §2. NB the two BPD slugs are CROSSED (audit/articles-audit.md §1): app `bpd-schema-therapy` (the
 * qualitative study) is website `/bpd-schema-therapy-2/`; app `bpd-st-effectiveness` (the effectiveness review) is
 * website `/bpd-schema-therapy/`. The acute-PTSD digest is `/ptsd-cbt/` on the website.
 */
export const LEARN_RETIRED_ARTICLES: Readonly<
  Record<string, LearnRetiredArticleEntry>
> = {
  'individual-therapy': {
    status: 'replacedBy',
    replacedBy: 'what-is-cbt',
    title: {
      en: 'Individual Psychotherapy',
      ru: 'Индивидуальная психотерапия',
    },
  },
  'important-tasks-management': {
    status: 'replacedBy',
    replacedBy: 'getting-active',
    title: {
      en: 'TIME MANAGEMENT OF IMPORTANT TASKS',
      ru: 'ТАЙМ-МЕНЕДЖМЕНТ ВАЖНОГО',
    },
  },
  'mindfulness-study': {
    status: 'movedToWebsite',
    movedTo: '/mindfulness-study/',
    title: {
      en: 'Mindfulness-meditation for development of self-consciousness, prevention and correction of its disorders',
      ru: 'Практики осознанности в развитии самосознания, коррекции и профилактике его нарушений',
    },
  },
  'ocd-fears': {
    status: 'movedToWebsite',
    movedTo: '/ocd-fears/',
    title: {
      en: 'Core fears in OCD: Development of a Semi-Structured Interview and Phenomenology',
      ru: 'Основные страхи при ОКР: проведение полуструктурированного интервью и феноменология',
    },
  },
  'cbt-effectiveness': {
    status: 'movedToWebsite',
    movedTo: '/cbt-effectiveness/',
    title: {
      en: 'The cognitive behavioral therapy effectiveness in major depressive disorder and anxiety disorders treatment',
      ru: 'Эффективность когнитивно-поведенческой терапии в лечении большого депрессивного расстройства и тревожных расстройств',
    },
  },
  'bpd-schema-therapy': {
    status: 'movedToWebsite',
    movedTo: '/bpd-schema-therapy-2/',
    title: {
      en: 'Schema therapy for borderline personality disorder: A qualitative study of patients’ perceptions',
      ru: 'Схема-терапия пограничного расстройства личности: качественное исследование восприятия пациентов',
    },
  },
  'bpd-st-effectiveness': {
    status: 'movedToWebsite',
    movedTo: '/bpd-schema-therapy/',
    title: {
      en: 'Schema Therapy effectiveness in borderline personality disorder therapy',
      ru: 'Эффективность схема-терапии в работе с ПРЛ',
    },
  },
  'cbt-cultural-historical-approach': {
    status: 'movedToWebsite',
    movedTo: '/cbt-cultural-historical-approach/',
    title: {
      en: 'Cultural-Historical Approach as a Methodological Basis for Cognitive Behavioural Psychotherapy',
      ru: 'Культурно-исторический подход как методологическая основа когнитивной поведенческой психотерапии',
    },
  },
  'cbt-ocd-study': {
    status: 'movedToWebsite',
    movedTo: '/cbt-ocd-study/',
    title: {
      en: 'The effects of cognitive behavioral therapy on resting-state functional brain network in drug-naive patients with obsessive-compulsive disorder',
      ru: 'Влияние когнитивно-поведенческой терапии на функциональную сеть мозга в состоянии покоя у не принимавших лекарственные препараты пациентов с обсессивно-компульсивным расстройством (ОКР)',
    },
  },
  'panic-disorder-cbt': {
    status: 'movedToWebsite',
    movedTo: '/panic-disorder-cbt/',
    title: {
      en: 'Effectiveness of cognitive behavioral group therapy for patients in panic disorder in a specialized unit',
      ru: 'Эффективность групповой когнитивно-поведенческой терапии в лечении панического расстройства в специализированном отделении',
    },
  },
  'acute-ptsd': {
    status: 'movedToWebsite',
    movedTo: '/ptsd-cbt/',
    title: {
      en: 'Treatment of Acute Post-traumatic Stress Disorder With Brief Cognitive Behavioral Therapy: A Randomized Controlled Trial',
      ru: 'Лечение острого посттравматического стрессового расстройства с помощью краткосрочной когнитивно-поведенческой терапии: рандомизированное контролируемое исследование',
    },
  },
  'sensations': {
    status: 'retired',
    title: { en: 'Sensations', ru: 'Ощущения' },
  },
  // Rewritten in E56, then retired before release (Q-learn-20 A, 2026-10-11): the app has no recorder, the method's
  // author has no standing in the field, and its research core is taught by mindfulness, testing a thought and
  // self-kindness. An old reference keeps the old app's title.
  'voice-recorder-method': {
    status: 'retired',
    title: {
      en: 'THE VOICE RECORDER METHOD, or Self-Analyst.',
      ru: 'МЕТОД ДИКТОФОНА, или сам себе аналитик.',
    },
  },
};

/** The Learn films' two languages. */
export type LearnFilmLanguage = 'en' | 'ru';

/** Where a film is on YouTube, once the founder has uploaded it (public, the CopingCard channel). */
export type LearnFilmUpload = { id: string; duration: number | null };

/**
 * THE ONE PLACE TO FILL IN A YOUTUBE ID. `<slug>:<language>` → `null` (not uploaded yet — the film does not
 * render anywhere) or `{ id, duration }` (seconds; `null` shows no length on the tile).
 * English: the ten new films (Q-learn-4 A). Russian: the eight generated films (Q-learn-6) — Dmitri's seven
 * lectures are below in `LEARN_RU_LECTURES` and keep their ids.
 */
export const LEARN_FILM_UPLOADS: Readonly<
  Record<string, LearnFilmUpload | null>
> = {
  'learn-what-is-cbt:en': { id: 'd0zo2W3UCzw', duration: 168 },
  'learn-abc:en': { id: '7JTGwVXuC5w', duration: 137 },
  'learn-thinking-traps:en': { id: 'IGWqE7MOvLs', duration: 121 },
  'learn-testing-a-thought:en': { id: 'xqeSuGTGKsc', duration: 125 },
  'learn-coping-cards:en': { id: '6b8BayHheHs', duration: 131 },
  'learn-core-beliefs:en': { id: 'd2ZQvQCsWnI', duration: 124 },
  'learn-vicious-cycles:en': { id: 'hKaSMxtceBQ', duration: 120 },
  'learn-exposure:en': { id: 'aKS2tyHv9xc', duration: 132 },
  'learn-worry:en': { id: 'IJP-AsN7s_s', duration: 112 },
  'learn-getting-active:en': { id: 'wJznGC46TVg', duration: 123 },
  'learn-abc:ru': { id: 'D5wngE2K7TQ', duration: 131 },
  'learn-thinking-traps:ru': { id: 'zAeO5vJ7_7U', duration: 124 },
  'learn-testing-a-thought:ru': { id: 'csd-QYxuqOM', duration: 128 },
  'learn-coping-cards:ru': { id: 'L6NsXuM0vHE', duration: 132 },
  'learn-vicious-cycles:ru': { id: 'WzVPeGn8XMs', duration: 115 },
  'learn-exposure:ru': { id: 'VCRSxz6MPAQ', duration: 132 },
  'learn-worry:ru': { id: 'VeJce55A1iI', duration: 115 },
  'learn-getting-active:ru': { id: 'EUH_R2KR-As', duration: 128 },
  // The second batch (founder 2026-10-21): Schema modes, Functional analysis, Motivational interviewing, Sleep; EN + RU.
  'learn-schema-modes:en': { id: '3OTbyQpIouI', duration: 131 },
  'learn-functional-analysis:en': { id: 'y3LDDH1L1ZU', duration: 134 },
  'learn-sleep:en': { id: 'NXdJ78-7scQ', duration: 129 },
  'learn-motivation:en': { id: 'L5PXvYnCji4', duration: 126 },
  'learn-schema-modes:ru': { id: 'B0fKdCrF2MI', duration: 126 },
  'learn-functional-analysis:ru': { id: 'lhM3EsoiaZI', duration: 134 },
  'learn-sleep:ru': { id: '4k-YExF8vqo', duration: 131 },
  'learn-motivation:ru': { id: 'mwk7gnY9JJw', duration: 125 },
};

/** Dmitri's seven Russian lectures (Q-learn-6: kept as they are, listed first in the Russian Videos list). */
export const LEARN_RU_LECTURES: ReadonlyArray<{ id: string; name: string }> = [
  {
    id: 'fHt-dkS-r6U',
    name: 'Доказательная психотерапия. Виды когнитивно-поведенческой психотерапии.',
  },
  {
    id: 'ndcA-AHHEHk',
    name: 'Когнитивно-поведенческая психотерапия (КПТ). Методология и этика',
  },
  {
    id: 'uQXBmyBK0j4',
    name: 'КПТ. Взаимосвязь когнитивных и эмоционнальных процессов.',
  },
  { id: '1AafsRPpvKE', name: 'Глубинные убеждения в КПТ' },
  { id: 'lIKOXr109Jg', name: 'Схема-терапия' },
  {
    id: 'fC2EbupiGl4',
    name: 'Посттравматическое стрессовое расстройство (ПТСР)',
  },
  {
    id: 'oEiAw94jq-g',
    name: 'Расстройства личности и когнитивно-поведенческая психотерапия (КПТ)',
  },
];

/** One language of a film: `id` is null until the founder uploads it; the film then does not render. */
export type LearnFilmVariant = {
  id: string | null;
  name: string;
  duration: number | null;
};

export type LearnFilm = {
  /** The film slug of BUILD.md §1 (`learn-abc`), or `lecture-<youtube id>` for one of Dmitri's seven. */
  slug: string;
  /** The Basics article this film belongs to; '' for a lecture. */
  articleId: string;
  en: LearnFilmVariant | null;
  ru: LearnFilmVariant | null;
};

/** BUILD.md §1 — the ten films in topic order: [slug, article id, a generated Russian film is made]. */
const NEW_FILMS: ReadonlyArray<readonly [string, string, boolean]> = [
  ['learn-what-is-cbt', 'what-is-cbt', false],
  ['learn-abc', 'abc', true],
  ['learn-thinking-traps', 'thinking-traps', true],
  ['learn-testing-a-thought', 'testing-a-thought', true],
  ['learn-coping-cards', 'coping-cards', true],
  ['learn-core-beliefs', 'core-beliefs', false],
  ['learn-schema-modes', 'schema-modes', true], // RU made: founder 2026-10-21 reversed Q-tail-3 B
  ['learn-vicious-cycles', 'vicious-cycles', true],
  ['learn-exposure', 'avoidance-and-exposure', true],
  ['learn-functional-analysis', 'functional-analysis', true],
  ['learn-worry', 'worry', true],
  ['learn-getting-active', 'getting-active', true],
  ['learn-sleep', 'sleep', true],
  ['learn-motivation', 'motivation', true],
];

const titleOf = (articleId: string, language: LearnFilmLanguage): string =>
  LEARN_BASICS.find((a) => a.id === articleId)?.title[language] ?? articleId;

const variantOf = (
  slug: string,
  articleId: string,
  language: LearnFilmLanguage,
): LearnFilmVariant => {
  const upload = LEARN_FILM_UPLOADS[`${slug}:${language}`] ?? null;
  return {
    id: upload?.id ?? null,
    name: titleOf(articleId, language),
    duration: upload?.duration ?? null,
  };
};

/**
 * The videos catalogue, in list order: the Russian lectures first (they have no English twin), then the ten
 * films in topic order. English shows the films with `en`, Russian the lectures and the films with `ru`.
 */
export const LEARN_FILMS: readonly LearnFilm[] = [
  ...LEARN_RU_LECTURES.map(
    (lecture): LearnFilm => ({
      slug: `lecture-${lecture.id}`,
      articleId: '',
      en: null,
      ru: { id: lecture.id, name: lecture.name, duration: null },
    }),
  ),
  ...NEW_FILMS.map(
    ([slug, articleId, hasRu]): LearnFilm => ({
      slug,
      articleId,
      en: variantOf(slug, articleId, 'en'),
      ru: hasRu ? variantOf(slug, articleId, 'ru') : null,
    }),
  ),
];

/**
 * Q-learn-7 A — the seven old English clips. They left the catalogue and the portal's pickers, but stay playable
 * where a worksheet or a homework step already names them, with the title they always had. Two of them share the
 * name "What is CBT" (the shipped quirk, kept).
 */
export const LEARN_RETIRED_VIDEOS: ReadonlyArray<{ id: string; name: string }> =
  [
    { id: '9c_Bv_FBE-c', name: 'What is CBT' },
    { id: 'bUOaHsxe8OQ', name: 'What is CBT' },
    { id: 'WRRdSm4ZjX4', name: 'ABC Model' },
    { id: 'RyWXTK4xDD0', name: 'Automatic Negative Thoughts' },
    { id: 'Yw1p9YlZKEU', name: 'Core Beliefs' },
    { id: 'qJpwEFTh1y0', name: 'Depression and Anxiety' },
    { id: '3VIL1L_ypMg', name: 'CBT Exercises' },
  ];

/**
 * Every article id the library knows and its title in one language: the 22 Basics, then the retired ids (an old
 * assignment keeps reading by the title it had). The portal's `homework.articles.<id>` labels are this table.
 */
export const learnArticleTitles = (
  language: LearnFilmLanguage,
): Record<string, string> => ({
  ...Object.fromEntries(
    Object.entries(LEARN_RETIRED_ARTICLES).map(([id, r]) => [
      id,
      r.title[language],
    ]),
  ),
  ...Object.fromEntries(LEARN_BASICS.map((a) => [a.id, a.title[language]])),
});

/**
 * A Learn clip's name by its YouTube id, in either language, else the retired English clips' old titles; undefined
 * for an id none of them knows. (The phone's `learnVideoNameOf` is the same lookup with the person's language first.)
 */
export const learnVideoNameById = (id: string): string | undefined =>
  LEARN_FILMS.map((f) => f.en).find((v) => v?.id === id)?.name ??
  LEARN_FILMS.map((f) => f.ru).find((v) => v?.id === id)?.name ??
  LEARN_RETIRED_VIDEOS.find((v) => v.id === id)?.name;
